import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { useState, type ReactNode } from "react";

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

/**
 * Defaults do QueryClient para o app inteiro.
 *
 * - `staleTime` moderado (30s): os dados do painel do organizador não mudam
 *   a cada teclado — evita refetch em toda troca de aba/foco sem cachear
 *   informação por tempo demais (o app tem KPIs "ao vivo").
 * - `refetchOnWindowFocus: false`: o app é usado em telão/kiosk/painel aberto
 *   o dia inteiro; refetch agressivo em foco de janela só gera ruído.
 * - `retry: 1`: uma tentativa extra basta para falhas de rede transitórias;
 *   não queremos martelar o backend em erro de autenticação/4xx (ver
 *   `ApiError` em `services/api/client.ts`, que não é "retryable" por
 *   natureza — mas o QueryClient não sabe disso, então mantemos o retry
 *   baixo para não mascarar erro real atrás de 3-4 tentativas).
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  // useState garante uma única instância por montagem do componente (e não
  // por módulo), o que importa em testes que montam/desmontam o app várias
  // vezes no mesmo processo.
  const [queryClient] = useState(createQueryClient);

  return (
    // `attribute="class"` casa com a estratégia `.dark` de src/styles/globals.css
    // (a Foundation deixou a classe pronta; quem aplica/persiste é o Shell).
    // `storageKey="ea-theme"`: MESMA chave de localStorage do legado
    // (app/web/js/app.js) — não perder a preferência de quem já usa o painel
    // antigo enquanto as duas UIs convivem. `defaultTheme="system"` +
    // `enableSystem`: sem escolha explícita, segue `prefers-color-scheme`;
    // uma alternância manual (ThemeToggle) sobrescreve a partir daí.
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="ea-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={200}>
          {children}
          <Toaster richColors closeButton position="top-right" />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
